package com.dotdrives.client

import com.dotdrives.config.BuildKonfig
import com.dotdrives.model.WorkflowFailure
import com.dotdrives.model.WorkflowRequest
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.auth
import io.ktor.client.HttpClient
import io.ktor.client.request.*
import io.ktor.client.statement.bodyAsText
import io.ktor.http.*
import kotlinx.serialization.json.*

/** Only authenticated, documented action endpoints; service RPCs are never exposed. */
class WorkflowClient(private val client: SupabaseClient = SupabaseProvider.client) {
    suspend fun execute(request: WorkflowRequest): JsonObject {
        require(
            request.endpoint in
                setOf("dot-admin", "workflows", "stripe-connect", "billing-portal", "qbo-connect", "qbo-sync")
        )
        val jwt =
            client.auth.currentSessionOrNull()?.accessToken ?: error("Sign in again to continue.")
        val http = HttpClient()
        try {
            val response =
                http.post(
                    "${BuildKonfig.SUPABASE_URL.trimEnd('/')}/functions/v1/${request.endpoint}"
                ) {
                    header("apikey", BuildKonfig.SUPABASE_PUBLISHABLE_KEY)
                    bearerAuth(jwt)
                    contentType(ContentType.Application.Json)
                    setBody(request.body.toString())
                }
            val envelope = Json.parseToJsonElement(response.bodyAsText()).jsonObject
            if (!response.status.isSuccess()) {
                val code = envelope["error"]?.jsonObject?.get("code")?.jsonPrimitive?.contentOrNull
                throw WorkflowFailure(
                    code,
                    when (code) {
                        "forbidden",
                        "unauthorized" ->
                            "Your account cannot complete this action. Check your role and, for invitations, your verified email."
                        "service_unavailable",
                        "provider_unavailable",
                        "stripe_not_configured",
                        "qbo_not_configured" ->
                            "This service is not available. Ask your administrator to finish provider setup, then retry."
                        "reconciliation_required" ->
                            "This operation needs administrator review before it can be retried."
                        "billing_rejected" ->
                            "This billing action was not sent. Refresh billing and choose an available action."
                        "checkout_expired" ->
                            "This checkout expired without payment. You can start a new purchase."
                        "conflict",
                        "payment_conflict" ->
                            "This action conflicts with current records. Refresh the status; an owner or a different active teammate may be required."
                        "invalid_request" -> "Check the entered details and try again."
                        else ->
                            "The action could not be completed. Retry the same request or contact your administrator."
                    },
                )
            }
            return envelope["data"]?.jsonObject
                ?: error("The service returned an incomplete response. Retry.")
        } finally {
            http.close()
        }
    }
}
